import React, { useMemo, useState } from "react";
import {
  Button,
  Container,
  Layout,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";
import { useAppSelector, useHandlePageNavigate } from "../../../hooks/hooks";
import ProtectionShell from "./ProtectionShell";
import { selectProtectionState } from "../../../features/profileProtection/profileProtectionSlice";

type EmailStage = "Immediate" | "+1 Hour" | "+1 Day" | "+1 Week";

interface EmailDraft {
  subject: string;
  body: string;
}

const stageOrder: EmailStage[] = ["Immediate", "+1 Hour", "+1 Day", "+1 Week"];

const EmailsPreview: React.FC = () => {
  const handleNavigate = useHandlePageNavigate();
  const { profileName } = useAppSelector(selectProtectionState);
  const [stage, setStage] = useState<EmailStage>("Immediate");
  const fullName = profileName || "Member";
  const firstName = fullName.split(" ")[0] || "Member";

  const drafts = useMemo<Record<EmailStage, EmailDraft[]>>(
    () => ({
      Immediate: [
        {
          subject: "We started your removals",
          body: `Hi ${firstName}, we submitted opt-out requests and started monitoring your profile across broker sources.`,
        },
      ],
      "+1 Hour": [
        {
          subject: "First removals complete",
          body: `Hi ${firstName}, your first broker removal has completed. We will keep checking for relisting events.`,
        },
      ],
      "+1 Day": [
        {
          subject: "Progress update",
          body: `Hi ${firstName}, multiple broker listings were removed. A few sources may still need confirmation.`,
        },
      ],
      "+1 Week": [
        {
          subject: "Weekly protection summary",
          body: `Hi ${firstName}, your protection remains active. We continue scanning and suppressing new exposures.`,
        },
      ],
    }),
    [firstName],
  );

  return (
    <ProtectionShell
      title="Email preview"
      subtitle="Prototype previews of member notifications through the removal timeline."
    >
      <Layout type="flex" flexProps={{ gap: "xs", wrap: "wrap" }}>
        {stageOrder.map(stageValue => (
          <Button
            key={stageValue}
            buttonText={stageValue}
            color={stage === stageValue ? "primary" : "secondary"}
            size="sm"
            handleClick={() => setStage(stageValue)}
          />
        ))}
      </Layout>

      <Layout type="flex" flexProps={{ direction: "column", gap: "sm" }}>
        {drafts[stage].map(draft => (
          <Container key={draft.subject}>
            <Padding space="sm">
              <Layout
                type="flex"
                flexProps={{ direction: "column", gap: "xxs" }}
              >
                <Text fontWeight="semibold">{draft.subject}</Text>
                <Text size="xs">{draft.body}</Text>
              </Layout>
            </Padding>
          </Container>
        ))}
      </Layout>

      <Layout type="flex" flexProps={{ justifyContent: "space-between" }}>
        <Button
          buttonText="Back"
          color="secondary"
          handleClick={() => handleNavigate("/protection/status")}
        />
      </Layout>
    </ProtectionShell>
  );
};

export default EmailsPreview;
